import React, {useEffect, useMemo} from 'react';
import {observer} from 'mobx-react-lite';
import './checkboxes-tree.scss';
import CheckboxesTreeStore, {ICheckboxesTreeItem} from './stores/checkboxes-tree-store';
import CheckboxesTreeItems from './checkboxes-tree-items/checkboxes-tree-items';
import {UtilityWrapper} from "./utility-wrapper/utility-wrapper";
import FontBody2 from "../../../fonts/font-body2/font-body2";
import {FONT_COLOR} from "../../../fonts/font-props";
import {TRISTATE_CHECKBOX_VALUES} from "../../tristate-checkbox/tristate-checkbox";
import NestedCheckboxesListSelectAll from "./checkboxes-tree-select-all/checkboxes-tree-select-all";
import CheckboxesTreeManager from './stores/checkboxes-tree-manager';


export interface ICheckboxesTreeProps {
    compressed?: boolean;
    options: ICheckboxesTreeItem[];
    value: ICheckboxesTreeItem[]|undefined;
    onChange?: (updatedData: ICheckboxesTreeItem[]|undefined) => void;
    searchTerm?: string;
    selectAllText?: string;
    isDisabled?: boolean;
    isInitiallyAllItemsExpanded?: boolean;
    onSummaryTextChange?: (text: string) => void;
    onVisibilityChange?: (isAllHidden: boolean) => void;
    onFilteringChange?: (isFiltering: boolean) => void;
}

const CheckboxesTree: React.FC<ICheckboxesTreeProps> = observer((
    {
        compressed = false,
        options = [],
        value,
        onChange,
        searchTerm, selectAllText,
        isDisabled,
        isInitiallyAllItemsExpanded,
        onSummaryTextChange,
        onVisibilityChange,
        onFilteringChange
    }
) => {
    const store = useMemo(() => new CheckboxesTreeStore(), []);
    const manager = useMemo(() => new CheckboxesTreeManager(options, compressed), [options, compressed]);
    
    useEffect(() => {
        manager.setValue(value);
        store.setItems(manager.getOptions());
    }, [value, store, manager]);

    useEffect(() => {
        store.setDisabled(Boolean(isDisabled));
    }, [isDisabled, store]);

    useEffect(() => {
        store.setOnChangeCallback((updatedValues: ICheckboxesTreeItem[]) => {
            const selectedValues = manager.extractSelectedValues(updatedValues);
            if (onChange) {
                onChange(selectedValues);
            }
        });
    }, [manager, onChange, store]);

    useEffect(() => {
        store.setFilterTerm(searchTerm ?? '');
    }, [searchTerm, store]);

    useEffect(() => {
        store.isAllExpanded(Boolean(isInitiallyAllItemsExpanded));
    }, [isInitiallyAllItemsExpanded, store]);

    const disabled = store.isDisabled();
    const selectAllState: TRISTATE_CHECKBOX_VALUES = store.getSelectAllState();
    const summaryText = store.getSummaryText();
    const isAllHidden = store.isAllHidden();
    const isFiltering = store.isFiltering();

    useEffect(() => {
        if (onSummaryTextChange) {
            onSummaryTextChange(summaryText);
        }
    }, [summaryText, onSummaryTextChange]);
    useEffect(() => {
        if (onVisibilityChange) {
            onVisibilityChange(isAllHidden);
        }
    }, [isAllHidden, onVisibilityChange]);
    useEffect(() => {
        if (onFilteringChange) {
            onFilteringChange(isFiltering);
        }
    }, [isFiltering, onFilteringChange]);

    const handleSelectAllChange = (newState: TRISTATE_CHECKBOX_VALUES) => {
        store.handleSelectAllChange(newState);
    };

    const toggleSelectAll = () => {
        if (disabled) {
            return;
        }
        store.toggleSelectAll();
    };

    return (
        <>
            <div className={`checkboxes-tree ${disabled ? 'disabled' : ''}`}>
                {!isFiltering && !isAllHidden &&
                    <>
                        {selectAllText && (
                            <NestedCheckboxesListSelectAll
                                disabled={disabled}
                                selectAllText={selectAllText}
                                value={selectAllState}
                                onToggle={toggleSelectAll}
                                onChange={handleSelectAllChange}
                            />
                        )}
                        <div className="checkboxes-tree-items-container">
                            <CheckboxesTreeItems store={store}/>
                        </div>
                    </>
                }
            </div>
            {!isFiltering && isAllHidden &&
                <UtilityWrapper>
                    <FontBody2 color={FONT_COLOR.NEUTRAL3}>No results</FontBody2>
                </UtilityWrapper>
            }
        </>
    );
});

export default CheckboxesTree;